import { css } from "@emotion/css";
import { Button } from "@octopusdeploy/design-system-components";
import { borderRadius, colorScales, space, text, themeTokens } from "@octopusdeploy/design-system-tokens";
import type { ArgoCDGatewayHealthStatus } from "@octopusdeploy/octopus-server-client";
import { links } from "@octopusdeploy/portal-routes";
import { useEffect, useState } from "react";
import { ArgoCDLogo } from "~/areas/infrastructure/components/ArgoCDInstances/ArgoCDLogo";
import { GatewayInstallationStatus } from "~/areas/infrastructure/components/ArgoCDInstances/DialogViews/components/GatewayInstallationStatus";
import { EnvironmentChip } from "../Chips";
import { useSpaceAwareNavigation } from "../Navigation/SpaceAwareNavigation/useSpaceAwareNavigation";
type RegisterArgoCDInstanceCardProps = {
    onClick: () => void;
};
type RegisteredArgoCDInstanceCardProps = {
    name: string;
    spaceId: string;
    environments?: string[];
    healthStatus: ArgoCDGatewayHealthStatus;
    id: string;
    close: () => void;
    forceNotFound?: boolean;
    bearerTokenExpired?: boolean;
    onInstanceAdded?: () => void;
};
export const RegisterArgoCDInstanceCard = ({ onClick }: RegisterArgoCDInstanceCardProps) => (<div className={styles.cardBase}>
        <div className={styles.iconContainer}>
            <ArgoCDLogo />
        </div>
        <div className={styles.title}>Register Argo CD instance</div>
        <div className={styles.addDescription}>Connect Octopus to your Argo CD instance by installing a gateway.</div>
        <div className={styles.addButtonContainer}>
            <Button label={"Register Instance"} onClick={onClick} importance={"secondary"}/>
        </div>
    </div>);
export const RegisteredArgoCDInstanceCard = (props: RegisteredArgoCDInstanceCardProps) => {
    const { spaceId, environments, name, healthStatus, id, close, forceNotFound = false, bearerTokenExpired = false, onInstanceAdded } = props;
    const hasNonHealthyStatus = healthStatus !== "Healthy";
    const [instanceAdded, setInstanceAdded] = useState(false);
    useEffect(() => {
        const targetIsHealthy = healthStatus === "Healthy";
        if (targetIsHealthy && !instanceAdded) {
            onInstanceAdded?.();
            setInstanceAdded(true);
        }
    }, [healthStatus, onInstanceAdded, instanceAdded]);
    const navigate = useSpaceAwareNavigation();
    const url = links.argoCDInstanceSettingsPage.generateUrl({ spaceId: spaceId, instanceId: id });
    const onClick = () => {
        close();
        navigate.navigate(url);
    };
    return (<div className={styles.cardBase}>
            <div className={styles.iconContainer}>
                <ArgoCDLogo />
            </div>

            <div>
                <div className={styles.title}>{name}</div>
                <div className={styles.subtitle}>Argo CD Instance</div>
            </div>

            <div className={styles.chipContainer}>
                {environments?.map((environment, index) => (<EnvironmentChip key={`${index}-${environment}`} environmentName={environment}/>))}
            </div>

            <div className={styles.discoveryProgressContainer}>
                <GatewayInstallationStatus healthStatus={healthStatus} isFound={healthStatus !== "Unknown"}/>
                {hasNonHealthyStatus && <Button label={"View Argo CD Instance"} onClick={onClick} importance={"secondary"}/>}
            </div>
        </div>);
};
const styles = {
    cardBase: css({
        width: "348px",
        padding: space[16],
        borderRadius: borderRadius.small,
        boxShadow: themeTokens.shadow.small,
        backgroundColor: themeTokens.color.card.background.primary.default,
        display: "flex",
        flexDirection: "column",
        gap: space[8],
    }),
    iconContainer: css({
        width: "40px",
        height: "40px",
        display: "flex",
        alignItems: "center",
        paddingLeft: space[4],
        backgroundColor: colorScales.white,
        borderRadius: borderRadius.small,
        "&& > div": {
            height: "32px",
        },
    }),
    title: css({ font: text.body.bold.large }),
    addDescription: css({
        font: text.body.regular.small,
        letterSpacing: "0.1px",
        color: themeTokens.color.text.secondary,
    }),
    subtitle: css({
        font: text.body.bold.xSmall,
        color: themeTokens.color.text.tertiary,
    }),
    addButtonContainer: css({
        paddingTop: "12px",
        "&& > button": {
            padding: space[6],
            font: text.body.bold.medium,
        },
    }),
    chipContainer: css({
        gap: space[8],
        display: "flex",
        justifyContent: "flex-start",
        flexWrap: "wrap",
        "&& > div": {
            margin: "0",
        },
    }),
    discoveryProgressContainer: css({
        marginTop: space[8],
        "& button": {
            marginTop: space[16],
            padding: `${space[8]} ${space[6]}`,
        },
    }),
};
